import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useSelectedOrganization } from "@/app/OrganizationContext";

import {
  getMyOrg,
  orgId as readOrgId,
  updateOrg,
  type Org,
  type OrgAddressInput,
} from "@/libs/onboardingApi";

function Row({
  capitalize = false,
  label,
  value,
}: {
  capitalize?: boolean;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: { xs: 0.5, sm: 2 },
        gridTemplateColumns: {
          xs: "1fr",
          sm: "minmax(8rem, auto) minmax(0, 1fr)",
        },
        py: 1.25,
      }}
    >
      <Typography color="text.secondary" component="dt" variant="body2">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{
          fontWeight: 700,
          m: 0,
          minWidth: 0,
          overflowWrap: "anywhere",
          textAlign: { xs: "left", sm: "right" },
          textTransform: capitalize ? "capitalize" : undefined,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

function SettingsSection({
  action,
  children,
  id,
  title,
}: {
  action?: React.ReactNode;
  children: React.ReactNode;
  id?: string;
  title: string;
}) {
  return (
    <Paper
      id={id}
      variant="outlined"
      sx={{ overflow: "hidden", scrollMarginTop: 80 }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 2.5 },
          py: 2,
        }}
      >
        <Typography component="h2" variant="h6" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      <Divider />
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>{children}</Box>
    </Paper>
  );
}

function LockedLabel() {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <LockOutlinedIcon fontSize="inherit" />
      <Typography color="text.secondary" variant="caption">
        Locked after approval
      </Typography>
    </Stack>
  );
}

const LOCKED_STATUSES = ["approved", "active", "suspended"];

export function OrgSettings() {
  const { organizationId } = useSelectedOrganization();
  return <SelectedOrgSettings key={organizationId ?? "unselected"} organizationId={organizationId} />;
}

function SelectedOrgSettings({ organizationId }: { organizationId: string | null }) {
  const [org, setOrg] = useState<Org | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [saving, setSaving] = useState(false);

  // Headquarters address editor
  const [editingAddr, setEditingAddr] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [addrNotice, setAddrNotice] = useState<string | null>(null);
  const [addrForm, setAddrForm] = useState<OrgAddressInput>({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const o = await getMyOrg(organizationId);
        if (cancelled) return;
        setOrg(o);
        setBusinessName(o.business_name ?? "");
        setLegalName(o.legal_name ?? "");
        setAddrForm({
          line1: o.address?.line1 ?? "",
          line2: o.address?.line2 ?? "",
          city: o.address?.city ?? "",
          state: o.address?.state ?? "",
          postal_code: o.address?.postal_code ?? "",
          country: o.address?.country ?? "",
        });
      } catch (err) {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : "Failed to load organization",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  if (loading) {
    return (
      <Box
        role="status"
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress
          aria-label="Loading organization settings"
          size={28}
        />
      </Box>
    );
  }

  if (error || !org) {
    return (
      <Alert severity="error" sx={{ mx: { xs: 2, lg: 3 } }}>
        {error ?? "No organization found."}
      </Alert>
    );
  }

  const locked = LOCKED_STATUSES.includes(org.status);
  const addr = org.address;

  // Backend sends these as objects/branded values, not plain strings.
  const payout = org.payout_schedule as unknown;
  const payoutText =
    typeof payout === "string"
      ? payout
      : payout && typeof payout === "object"
        ? String((payout as { frequency?: string }).frequency ?? "—")
        : null;
  const commission = org.commission_rate as unknown;
  const commissionText =
    typeof commission === "number" || typeof commission === "string"
      ? `${commission}`
      : null;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateOrg(readOrgId(org), {
        business_name: businessName,
        legal_name: legalName,
      });
      setOrg(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveAddr = async () => {
    setSavingAddr(true);
    setError(null);
    setAddrNotice(null);
    try {
      const updated = await updateOrg(readOrgId(org), {
        address: {
          line1: addrForm.line1.trim(),
          line2: addrForm.line2?.trim() || undefined,
          city: addrForm.city.trim(),
          state: addrForm.state?.trim() || undefined,
          postal_code: addrForm.postal_code.trim(),
          country: addrForm.country.trim().toUpperCase(),
        },
      });
      setOrg(updated);
      setEditingAddr(false);
      setAddrNotice("Headquarters address saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSavingAddr(false);
    }
  };

  const addrComplete =
    addrForm.line1.trim() !== "" &&
    addrForm.city.trim() !== "" &&
    addrForm.postal_code.trim() !== "" &&
    addrForm.country.trim().length === 2;

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 }, pb: 4 }}>
      <Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          <Typography component="h1" variant="h5" sx={{ fontWeight: 800 }}>
            Organization Settings
          </Typography>
          <Chip
            label={org.status.replace("_", " ")}
            size="small"
            sx={{ textTransform: "capitalize" }}
          />
        </Stack>
        <Typography color="text.secondary" variant="body2">
          Business profile, contacts, and commercial terms.
        </Typography>
      </Box>

      <SettingsSection
        action={
          locked ? (
            <LockedLabel />
          ) : editing ? null : (
            <Button
              onClick={() => setEditing(true)}
              size="small"
              variant="outlined"
            >
              Edit
            </Button>
          )
        }
        title="Business profile"
      >
        {editing && !locked ? (
          <Stack spacing={2}>
            <TextField
              fullWidth
              id="bn"
              label="Business name"
              onChange={(event) => setBusinessName(event.target.value)}
              size="small"
              value={businessName}
            />
            <TextField
              fullWidth
              id="ln"
              label="Legal name"
              onChange={(event) => setLegalName(event.target.value)}
              size="small"
              value={legalName}
            />
            <Stack direction="row" spacing={1}>
              <Button
                disabled={saving}
                onClick={save}
                size="small"
                variant="contained"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                disabled={saving}
                onClick={() => setEditing(false)}
                size="small"
                variant="outlined"
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack component="dl" divider={<Divider flexItem />} sx={{ m: 0 }}>
            <Row label="Business name" value={org.business_name} />
            <Row label="Legal name" value={org.legal_name} />
            <Row label="Business type" value={org.business_type} />
            <Row label="Tax ID" value={org.tax_id} />
            <Row label="Currency" value={org.currency} />
          </Stack>
        )}
      </SettingsSection>

      <SettingsSection title="Owner">
        <Stack component="dl" divider={<Divider flexItem />} sx={{ m: 0 }}>
          <Row
            label="Name"
            value={org.owner_full_name ?? org.owner_display_name}
          />
          <Row label="Email" value={org.owner_email} />
          <Row label="Phone" value={org.owner_phone} />
          <Row label="Position" value={org.owner_position} />
        </Stack>
      </SettingsSection>

      <SettingsSection
        action={
          locked ? (
            <LockedLabel />
          ) : editingAddr ? null : (
            <Button
              onClick={() => {
                setAddrNotice(null);
                setEditingAddr(true);
              }}
              size="small"
              variant="outlined"
            >
              Edit
            </Button>
          )
        }
        id="headquarters-address"
        title="Headquarters address"
      >
        {editingAddr && !locked ? (
          <Stack spacing={2}>
            <TextField
              fullWidth
              id="addr-line1"
              label="Street address"
              onChange={(event) =>
                setAddrForm((previous) => ({
                  ...previous,
                  line1: event.target.value,
                }))
              }
              size="small"
              value={addrForm.line1}
            />
            <TextField
              fullWidth
              id="addr-line2"
              label="Address line 2 (optional)"
              onChange={(event) =>
                setAddrForm((previous) => ({
                  ...previous,
                  line2: event.target.value,
                }))
              }
              size="small"
              value={addrForm.line2 ?? ""}
            />
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              <TextField
                id="addr-city"
                label="City"
                onChange={(event) =>
                  setAddrForm((previous) => ({
                    ...previous,
                    city: event.target.value,
                  }))
                }
                size="small"
                value={addrForm.city}
              />
              <TextField
                id="addr-state"
                label="State / province (optional)"
                onChange={(event) =>
                  setAddrForm((previous) => ({
                    ...previous,
                    state: event.target.value,
                  }))
                }
                size="small"
                value={addrForm.state ?? ""}
              />
              <TextField
                id="addr-postal"
                label="Postal code"
                onChange={(event) =>
                  setAddrForm((previous) => ({
                    ...previous,
                    postal_code: event.target.value,
                  }))
                }
                size="small"
                value={addrForm.postal_code}
              />
              <TextField
                id="addr-country"
                label="Country (2-letter code)"
                onChange={(event) =>
                  setAddrForm((previous) => ({
                    ...previous,
                    country: event.target.value,
                  }))
                }
                placeholder="US"
                size="small"
                slotProps={{ htmlInput: { maxLength: 2 } }}
                value={addrForm.country}
              />
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                disabled={savingAddr || !addrComplete}
                onClick={saveAddr}
                size="small"
                variant="contained"
              >
                {savingAddr ? "Saving…" : "Save"}
              </Button>
              <Button
                disabled={savingAddr}
                onClick={() => setEditingAddr(false)}
                size="small"
                variant="outlined"
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Stack component="dl" divider={<Divider flexItem />} sx={{ m: 0 }}>
              <Row
                label="Street"
                value={[addr?.line1, addr?.line2].filter(Boolean).join(", ")}
              />
              <Row label="City" value={addr?.city} />
              <Row label="State" value={addr?.state} />
              <Row label="Postal code" value={addr?.postal_code} />
              <Row label="Country" value={addr?.country} />
            </Stack>
            {addrNotice ? (
              <Typography color="text.secondary" role="status" variant="body2">
                {addrNotice}
              </Typography>
            ) : null}
          </Stack>
        )}
      </SettingsSection>

      <SettingsSection title="Payments & terms">
        <Stack component="dl" divider={<Divider flexItem />} sx={{ m: 0 }}>
          <Row
            label="Stripe"
            value={
              org.stripe_connect_id ? (
                <Chip
                  color={org.stripe_onboarding_complete ? "success" : "warning"}
                  label={
                    org.stripe_onboarding_complete ? "Connected" : "Pending"
                  }
                  size="small"
                  variant="outlined"
                />
              ) : (
                "Not connected"
              )
            }
          />
          <Row label="Commission rate" value={commissionText} />
          <Row capitalize label="Payout schedule" value={payoutText} />
          <Row
            label="Contract accepted"
            value={
              org.contract_terms_accepted_at
                ? new Date(org.contract_terms_accepted_at).toLocaleDateString()
                : "Not accepted"
            }
          />
        </Stack>
      </SettingsSection>
    </Stack>
  );
}
